import AppConstants from '../Constants/AppConstants';

const FIELDS = {
  more: ['statusValue'],
  forceUpdate: ['androidVersion', 'iosVersion'],
  splash: ['selectedPrimaryColor', 'selectedSplashImage'],
  chat: ['statusValue', 'negotiateValue', 'negotiateLabel'],
  address: ['statusArray'],
  persistentNotification: ['accountString', 'listingString', 'orderString'],
  registration: ['register', 'otpNote', 'signUpSwitchValue', 'enableMobileValue'],
  appDetails: ['appName', 'selectedPrimaryColor', 'selectedSecondaryColor'],
  onboarding: ['onBoardingTextArray', 'imagesTouched', 'imageScreen1', 'imageScreen2', 'imageScreen3'],
  homePage: ['storeFollowTitle', 'nearByTitle', 'latestProductTitle', 'statusArray', 'appTitleHome', 'sellIconText', 'appHomeIconSwitch', 'selectedImage', 'appTitleIconImage'],
  homeAppearance: ['register', 'sellIconText', 'appTitleHome', 'statusArray', 'switchValue', 'selectedImage', 'notificationIconImage', 'appTitleIconImage'],
  webGeneral: ['webIconImage', 'webLogoImage', 'webCoverImage', 'webFontTitle', 'webFontBody', 'webPrimaryFont', 'webName', 'logoWidth', 'logoHeight'],
  webFooter: ['iosIconImage', 'androidIconImage', 'iosAppDownloadLink', 'androidAppDownloadLink'],
  webSocial: ['textFieldArray'],
  otherSettings: ['statusValue', 'logSwitchValue', 'signUpSwitchValue'],
};

const sanitizeImage = value => {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return undefined;
  if (typeof value.base64 === 'string' && value.base64.length > 900000) return undefined;
  if (typeof value.image === 'string' && value.image.startsWith('data:image/') && value.image.length > 900000) return undefined;
  const base64 = typeof value.base64 === 'string' ? value.base64 : undefined;
  const image = typeof value.image === 'string' ? value.image : undefined;
  const name = typeof value.name === 'string' ? value.name : undefined;
  const type = typeof value.type === 'string' ? value.type : undefined;
  const file = Array.isArray(value.fileList) ? value.fileList[0] : null;
  if (file && !base64) return undefined;
  const fileList = file && typeof file.name === 'string'
    ? [{name: file.name, type: typeof file.type === 'string' ? file.type : ''}]
    : undefined;
  const safe = Object.fromEntries(Object.entries({base64, image, name, type, fileList}).filter(([, item]) => item !== undefined));
  return Object.keys(safe).length ? safe : undefined;
};

const sanitizeField = (panel, field, value) => {
  if (panel === 'onboarding' && /^imageScreen[123]$/.test(field) && value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return {};
  if (['selectedSplashImage', 'selectedAppIconAndroid', 'selectedAppIconIos', 'selectedImage', 'notificationIconImage', 'appTitleIconImage', 'webIconImage', 'webLogoImage', 'webCoverImage', 'iosIconImage', 'androidIconImage', 'imageScreen1', 'imageScreen2', 'imageScreen3'].includes(field)) return sanitizeImage(value);
  if (['webFontTitle', 'webFontBody', 'webPrimaryFont', 'textFieldArray'].includes(field) && Array.isArray(value)) return value.slice(0, 100).map(item => typeof item === 'string' ? item.slice(0, 2000) : '');
  if (field === 'onBoardingTextArray' && Array.isArray(value)) return value.map(item => ({text: typeof item?.text === 'string' ? item.text : ''}));
  if (field === 'statusArray' && Array.isArray(value)) return value.filter(item => typeof item === 'boolean');
  return ['string', 'boolean', 'number'].includes(typeof value) ? value : undefined;
};

const keyFor = panel =>
  `arcticwhale:${AppConstants.tenantName || 'current-tenant'}:${AppConstants.userId || 'current-user'}:app-customization:${panel}`;

export const readAppCustomizationDraft = panel => {
  if (typeof window === 'undefined' || !FIELDS[panel]) return {};
  try {
    const value = JSON.parse(window.localStorage.getItem(keyFor(panel)) || 'null');
    if (!value || typeof value !== 'object') return {};
    const draft = Object.fromEntries(FIELDS[panel].filter(field => field in value).map(field => [field, sanitizeField(panel, field, value[field])]).filter(([, fieldValue]) => fieldValue !== undefined));
    if (panel === 'onboarding' && draft.imagesTouched) draft.imagesArray = [draft.imageScreen1, draft.imageScreen2, draft.imageScreen3].filter(item => item?.image);
    return JSON.stringify(draft).length <= 2400000 ? draft : {};
  } catch (error) {
    return {};
  }
};

export const writeAppCustomizationDraft = (panel, values) => {
  if (typeof window === 'undefined' || !FIELDS[panel]) return;
  try {
    const safe = Object.fromEntries(FIELDS[panel].filter(field => field in values).map(field => [field, sanitizeField(panel, field, values[field])]).filter(([, fieldValue]) => fieldValue !== undefined));
    const serialized = JSON.stringify(safe);
    if (serialized.length <= 2400000) window.localStorage.setItem(keyFor(panel), serialized);
  } catch (error) {
    // Keep settings usable when browser storage is unavailable.
  }
};

export const clearAppCustomizationDraft = panel => {
  if (typeof window === 'undefined' || !FIELDS[panel]) return;
  try {
    window.localStorage.removeItem(keyFor(panel));
  } catch (error) {
    // Ignore storage errors after a successful save.
  }
};
